// =============================================================================
// PEDRA — src/lib/printPdf.ts
// Relatorio para impressao / "Salvar como PDF" e exportacao do diagrama.
//
// Nao existe biblioteca de PDF aqui: o PDF e a propria impressao do navegador.
// Montamos um container dedicado (`#pedra-print-root`) com o relatorio inteiro
// e um bloco `@media print` que esconde o resto da pagina. Quem manda no papel
// e o navegador — o resultado sai vetorial, com texto selecionavel e sem peso
// nenhum de bundle.
//
// Tambem exporta cada diagrama em SVG (vetorial) e em PNG (via canvas).
// =============================================================================

import type {
  EdgeBandType,
  OptimizationResult,
  Settings,
  SheetLayout,
} from '../core/types';
import {
  formatArea,
  formatBigLength,
  formatDimensions,
  formatLength,
  formatPercent,
  fromMicron,
} from '../core/units';
import { downloadBlob } from './csv';
import {
  cutAxisLabel,
  formatCount,
  formatDateTime,
  formatDuration,
  priorityLabel,
  yesNo,
} from './format';

// -----------------------------------------------------------------------------
// OPCOES
// -----------------------------------------------------------------------------

export interface PrintOptions {
  /** Capa com o resumo global do calculo. */
  includeSummary: boolean;
  /** Lista de cortes por chapa. */
  includeCuts: boolean;
  /** Tabela de pecas de cada chapa. */
  includeParts: boolean;
  /** Carimbo de data e hora no cabecalho. */
  includeDate: boolean;
  /** Texto livre no topo (nome da marcenaria, cliente, obra). */
  headerText: string;
  /** Observacoes livres impressas depois do resumo. */
  additionalText: string;
}

export const DEFAULT_PRINT_OPTIONS: PrintOptions = {
  includeSummary: true,
  includeCuts: true,
  includeParts: true,
  includeDate: true,
  headerText: '',
  additionalText: '',
};

/** Id do container criado para imprimir. */
export const PRINT_ROOT_ID = 'pedra-print-root';

/** Largura base do PNG antes de aplicar a escala. */
export const PNG_BASE_WIDTH = 1200;

/** Um diagrama ja serializado, pronto para entrar no relatorio. */
export interface PrintDiagram {
  /** `SheetLayout.index` da chapa correspondente. */
  sheetIndex: number;
  /** Markup do SVG (use `svgToString`). */
  svg: string;
}

/** Tudo que o relatorio precisa. Nao depende de React nem do estado do app. */
export interface PrintReportData {
  projectName: string;
  result: OptimizationResult;
  settings: Settings;
  edgeBands: readonly EdgeBandType[];
  /** Um item por chapa. Chapas sem diagrama saem so com as tabelas. */
  diagrams: readonly PrintDiagram[];
}

// -----------------------------------------------------------------------------
// SVG — SERIALIZACAO
// -----------------------------------------------------------------------------

/**
 * Variaveis de tema usadas pelo diagrama. Elas moram no `:root`, e um SVG
 * arrancado do documento nao as enxerga — por isso resolvemos os valores e
 * gravamos inline no clone antes de exportar.
 */
const THEME_VARS: readonly string[] = [
  '--sheet-bg',
  '--sheet-border',
  '--waste-hatch',
  '--offcut-hatch',
  '--cut-line',
  '--cut-line-active',
  '--dimension-line',
  '--part-stroke',
  '--part-text',
  '--text',
  '--text-muted',
  '--border',
  '--bg',
  '--surface',
  '--accent',
  '--font-sans',
  '--font-mono',
];

interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Le o `viewBox` do SVG. `null` quando ele nao existe ou esta malformado. */
export function readViewBox(svg: SVGSVGElement): ViewBox | null {
  const raw = svg.getAttribute('viewBox');
  if (!raw) return null;
  const parts = raw.trim().split(/[\s,]+/).map(Number);
  if (parts.length < 4 || parts.some((n) => !Number.isFinite(n))) return null;
  const [x, y, w, h] = parts as [number, number, number, number];
  if (w <= 0 || h <= 0) return null;
  return { x, y, w, h };
}

/** Copia os valores calculados das variaveis de tema para o clone. */
function inlineThemeVariables(source: SVGSVGElement, clone: SVGSVGElement): void {
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return;
  let computed: CSSStyleDeclaration;
  try {
    computed = window.getComputedStyle(source);
  } catch {
    return;
  }
  for (const name of THEME_VARS) {
    const value = computed.getPropertyValue(name).trim();
    if (value !== '') clone.style.setProperty(name, value);
  }
}

/**
 * Serializa um `<svg>` do DOM em markup autocontido: namespaces declarados,
 * dimensoes explicitas e variaveis de tema resolvidas. Sem declaracao XML —
 * o texto pode ser inserido direto em HTML.
 */
export function svgToString(svg: SVGSVGElement): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  inlineThemeVariables(svg, clone);

  const box = readViewBox(clone);
  if (box) {
    clone.setAttribute('width', String(box.w));
    clone.setAttribute('height', String(box.h));
    clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  }

  if (typeof XMLSerializer === 'undefined') return clone.outerHTML ?? '';
  return new XMLSerializer().serializeToString(clone);
}

/** Encontra os diagramas dentro de um container e ja os serializa. */
export function collectDiagrams(root: ParentNode, selector = 'svg[data-sheet-index]'): PrintDiagram[] {
  const out: PrintDiagram[] = [];
  const nodes = root.querySelectorAll(selector);
  nodes.forEach((node, i) => {
    const svg = node as SVGSVGElement;
    const attr = Number(svg.getAttribute('data-sheet-index'));
    out.push({
      sheetIndex: Number.isFinite(attr) ? attr : i,
      svg: svgToString(svg),
    });
  });
  return out;
}

// -----------------------------------------------------------------------------
// SVG — DOWNLOAD
// -----------------------------------------------------------------------------

/** Baixa o diagrama em SVG (vetorial, abre no Illustrator/Inkscape/CorelDRAW). */
export function downloadSvg(svg: SVGSVGElement, filename: string): void {
  if (typeof Blob === 'undefined') return;
  const markup = `<?xml version="1.0" encoding="UTF-8"?>\n${svgToString(svg)}`;
  downloadBlob(filename, new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }));
}

function clampScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 1;
  return Math.max(0.25, Math.min(8, scale));
}

/**
 * Converte o diagrama em PNG usando `<canvas>`. O SVG e autocontido (sem fonte
 * nem imagem externa), condicao para o canvas nao ficar "tainted".
 */
export function svgToPngBlob(svg: SVGSVGElement, scale = 2): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    if (typeof document === 'undefined' || typeof Image === 'undefined') {
      reject(new Error('A exportação em PNG só funciona no navegador.'));
      return;
    }

    const markup = svgToString(svg);
    const box = readViewBox(svg);
    const ratio = box ? box.h / box.w : 1;
    const width = Math.max(1, Math.round(PNG_BASE_WIDTH * clampScale(scale)));
    const height = Math.max(1, Math.round(width * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Não foi possível gerar a imagem: este navegador não suporta canvas.'));
      return;
    }

    const image = new Image();
    image.onload = () => {
      try {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(image, 0, 0, width, height);
      } catch {
        reject(new Error('Não foi possível desenhar o diagrama na imagem.'));
        return;
      }
      if (typeof canvas.toBlob !== 'function') {
        reject(new Error('Este navegador não consegue salvar a imagem em PNG.'));
        return;
      }
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Não foi possível gerar o PNG do diagrama.'));
      }, 'image/png');
    };
    image.onerror = () => {
      reject(new Error('Não foi possível converter o diagrama em imagem.'));
    };
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  });
}

/** Gera o PNG e dispara o download. */
export async function downloadPng(
  svg: SVGSVGElement,
  filename: string,
  scale = 2,
): Promise<void> {
  const blob = await svgToPngBlob(svg, scale);
  downloadBlob(filename, blob);
}

// -----------------------------------------------------------------------------
// HTML DO RELATORIO
// -----------------------------------------------------------------------------

/** Escapa texto para entrar em HTML com seguranca. */
export function escapeHtml(value: string): string {
  return (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Uma linha `<tr>` com celulas ja escapadas. */
function tr(cells: ReadonlyArray<string | number>, tag: 'td' | 'th' = 'td'): string {
  const inner = cells.map((c) => `<${tag}>${escapeHtml(String(c))}</${tag}>`).join('');
  return `<tr>${inner}</tr>`;
}

/** Tabela completa com cabecalho. */
function table(
  headers: readonly string[],
  rows: ReadonlyArray<ReadonlyArray<string | number>>,
  emptyMessage: string,
): string {
  if (rows.length === 0) {
    return `<p class="pedra-print-empty">${escapeHtml(emptyMessage)}</p>`;
  }
  const head = tr(headers, 'th');
  const body = rows.map((r) => tr(r)).join('');
  return `<table class="pedra-print-table"><thead>${head}</thead><tbody>${body}</tbody></table>`;
}

/** Bloco `chave: valor` do resumo. */
function summaryRows(rows: ReadonlyArray<readonly [string, string]>): string {
  const items = rows
    .map(
      ([k, v]) =>
        `<div class="pedra-print-kv"><span>${escapeHtml(k)}</span><strong>${escapeHtml(v)}</strong></div>`,
    )
    .join('');
  return `<div class="pedra-print-summary">${items}</div>`;
}

// -----------------------------------------------------------------------------
// SECOES DO RELATORIO
// -----------------------------------------------------------------------------

/** Capa: cabecalho, data, resumo global, configuracoes e observacoes. */
function buildCover(data: PrintReportData, options: PrintOptions): string {
  const { result, settings } = data;
  const stats = result.stats;
  const title = options.headerText.trim() !== '' ? options.headerText.trim() : data.projectName;
  const subtitle = options.headerText.trim() !== '' ? data.projectName : '';

  const parts: string[] = [];
  parts.push('<header class="pedra-print-header">');
  parts.push(`<h1>${escapeHtml(title)}</h1>`);
  if (subtitle !== '') parts.push(`<p class="pedra-print-subtitle">${escapeHtml(subtitle)}</p>`);
  if (options.includeDate) {
    parts.push(`<p class="pedra-print-date">${escapeHtml(formatDateTime(new Date()))}</p>`);
  }
  parts.push('</header>');

  if (!options.includeSummary) return parts.join('');

  parts.push('<h2>Resumo</h2>');
  parts.push(
    summaryRows([
      ['Chapas utilizadas', formatCount(stats.numSheets)],
      ['Área total das chapas', formatArea(stats.totalSheetArea, settings.unit)],
      ['Área aproveitada', formatArea(stats.totalUsedArea, settings.unit)],
      ['Aproveitamento', formatPercent(stats.usedAreaRatio)],
      ['Área desperdiçada', formatArea(stats.totalWasteArea, settings.unit)],
      ['Desperdício', formatPercent(stats.wasteRatio)],
      ['Total de cortes', formatCount(stats.numCuts)],
      ['Comprimento total de corte', formatBigLength(stats.totalCutLength, settings.unit)],
      ['Peças posicionadas', formatCount(stats.numPlacedParts)],
      ['Peças não posicionadas', formatCount(stats.numUnfitParts)],
      ['Tempo de cálculo', formatDuration(stats.elapsedMs)],
    ]),
  );

  parts.push('<h2>Configurações usadas</h2>');
  parts.push(
    summaryRows([
      ['Espessura de corte', formatLength(settings.kerf, settings.unit, settings.decimalPlaces)],
      ['Prioridade', priorityLabel(settings.priority)],
      ['Considerar veio', yesNo(settings.considerGrain)],
      ['Considerar material', yesNo(settings.considerMaterial)],
      [
        'Sobra mínima aproveitável',
        formatLength(settings.minTrimDimension, settings.unit, settings.decimalPlaces),
      ],
    ]),
  );

  const bandTotals = Object.entries(data.result.stats.edgeBandingTotals ?? {});
  if (bandTotals.length > 0) {
    const rows = bandTotals.map(([id, total]) => {
      const band = data.edgeBands.find((b) => b.id === id);
      return [band ? band.name : id, formatBigLength(total, settings.unit)];
    });
    parts.push('<h2>Fita de borda</h2>');
    parts.push(table(['Fita', 'Comprimento'], rows, 'Nenhuma fita de borda utilizada.'));
  }

  if (result.warnings.length > 0) {
    parts.push('<h2>Avisos</h2>');
    parts.push(
      `<ul class="pedra-print-warnings">${result.warnings
        .map((w) => `<li>${escapeHtml(w)}</li>`)
        .join('')}</ul>`,
    );
  }

  const extra = options.additionalText.trim();
  if (extra !== '') {
    parts.push(`<p class="pedra-print-notes">${escapeHtml(extra)}</p>`);
  }

  return parts.join('');
}

/** Agrupa as pecas iguais de uma chapa para a tabela por chapa. */
function buildSheetPartsTable(sheet: SheetLayout, settings: Settings): string {
  interface Group {
    label: string;
    length: number;
    width: number;
    material: string;
    rotated: boolean;
    qty: number;
  }
  const groups = new Map<string, Group>();
  for (const pl of sheet.placements) {
    const key = `${pl.label}|${pl.length}|${pl.width}|${pl.material}|${pl.rotated ? 1 : 0}`;
    const hit = groups.get(key);
    if (hit) {
      hit.qty++;
      continue;
    }
    groups.set(key, {
      label: pl.label,
      length: pl.length,
      width: pl.width,
      material: pl.material,
      rotated: pl.rotated,
      qty: 1,
    });
  }

  const rows = [...groups.values()].map((g, i) => [
    i + 1,
    g.label === '' ? '—' : g.label,
    formatDimensions(g.length, g.width, settings.unit, settings.decimalPlaces, settings.dimensionOrder),
    formatCount(g.qty),
    g.material === '' ? '—' : g.material,
    yesNo(g.rotated),
  ]);

  return table(
    ['#', 'Nome', 'Dimensões', 'Qtd', 'Material', 'Girada'],
    rows,
    'Nenhuma peça foi posicionada nesta chapa.',
  );
}

/** Sequencia de cortes da chapa, na ordem de execucao. */
function buildSheetCutsTable(sheet: SheetLayout, settings: Settings): string {
  const rows = sheet.cuts.map((cut) => [
    cut.seq,
    cut.stage,
    cutAxisLabel(cut.axis),
    `${cut.axis} = ${fromMicron(cut.pos, settings.unit, settings.decimalPlaces)}`,
    fromMicron(cut.length, settings.unit, settings.decimalPlaces),
    `${fromMicron(cut.start, settings.unit, settings.decimalPlaces)} → ${fromMicron(
      cut.end,
      settings.unit,
      settings.decimalPlaces,
    )}`,
  ]);
  return table(
    ['#', 'Estágio', 'Eixo', 'Posição', 'Comprimento', 'Trecho'],
    rows,
    'Nenhum corte necessário nesta chapa.',
  );
}

/** Sobras da chapa, marcando o que da para reaproveitar. */
function buildSheetOffcutsTable(sheet: SheetLayout, settings: Settings): string {
  const rows = sheet.offcuts.map((off, i) => [
    i + 1,
    formatDimensions(off.length, off.width, settings.unit, settings.decimalPlaces, settings.dimensionOrder),
    `${fromMicron(off.x, settings.unit, settings.decimalPlaces)} · ${fromMicron(
      off.y,
      settings.unit,
      settings.decimalPlaces,
    )}`,
    formatArea(off.area, settings.unit),
    yesNo(off.usable),
  ]);
  return table(
    ['#', 'Dimensões', 'Posição (x · y)', 'Área', 'Aproveitável'],
    rows,
    'Esta chapa não deixou sobras.',
  );
}

/** Uma pagina por chapa: cabecalho, diagrama, rodape e as tabelas. */
function buildSheetSection(
  sheet: SheetLayout,
  total: number,
  diagram: PrintDiagram | undefined,
  data: PrintReportData,
  options: PrintOptions,
): string {
  const { settings } = data;
  const bits: string[] = [];

  const heading: string[] = [`Chapa ${sheet.index + 1} de ${total}`];
  if (sheet.label.trim() !== '') heading.push(sheet.label.trim());
  heading.push(
    formatDimensions(sheet.length, sheet.width, settings.unit, settings.decimalPlaces, settings.dimensionOrder),
  );
  if (sheet.material.trim() !== '') heading.push(sheet.material.trim());
  if (sheet.repeat > 1) heading.push(`${formatCount(sheet.repeat)} chapas idênticas`);

  bits.push('<section class="pedra-print-sheet">');
  bits.push(`<h2>${escapeHtml(heading.join(' — '))}</h2>`);

  if (diagram) {
    bits.push(`<div class="pedra-print-diagram">${diagram.svg}</div>`);
  }

  bits.push(
    `<p class="pedra-print-sheetfoot">${escapeHtml(
      [
        `Aproveitamento ${formatPercent(sheet.usedAreaRatio)}`,
        `${formatCount(sheet.numCuts)} cortes`,
        `${formatBigLength(sheet.cutLength, settings.unit)} de corte`,
        `${formatCount(sheet.placements.length)} peças`,
      ].join('  ·  '),
    )}</p>`,
  );

  if (options.includeParts) {
    bits.push('<h3>Peças desta chapa</h3>');
    bits.push(buildSheetPartsTable(sheet, settings));
  }

  if (options.includeCuts) {
    bits.push('<h3>Sequência de cortes</h3>');
    bits.push(buildSheetCutsTable(sheet, settings));
  }

  bits.push('<h3>Sobras</h3>');
  bits.push(buildSheetOffcutsTable(sheet, settings));
  bits.push('</section>');

  return bits.join('');
}

/** Bloco final com as pecas que nao couberam. */
function buildUnfitSection(data: PrintReportData): string {
  const { result, settings } = data;
  if (result.unfit.length === 0) return '';
  const rows = result.unfit.map((u) => [
    u.label === '' ? '—' : u.label,
    formatDimensions(u.length, u.width, settings.unit, settings.decimalPlaces, settings.dimensionOrder),
    formatCount(u.qty),
    u.material === '' ? '—' : u.material,
    u.message,
  ]);
  return [
    '<section class="pedra-print-sheet pedra-print-unfit">',
    '<h2>Peças que não couberam</h2>',
    table(['Nome', 'Dimensões', 'Qtd', 'Material', 'Motivo'], rows, ''),
    '</section>',
  ].join('');
}

// -----------------------------------------------------------------------------
// MONTAGEM E IMPRESSAO
// -----------------------------------------------------------------------------

/** CSS do relatorio. Fica escondido na tela e so aparece na impressao. */
export function buildPrintStyles(): string {
  return `<style>
#${PRINT_ROOT_ID} { display: none; }
@media print {
  @page { margin: 12mm; }
  html, body { background: #fff !important; }
  body > *:not(#${PRINT_ROOT_ID}) { display: none !important; }
  #${PRINT_ROOT_ID} {
    display: block !important;
    color: #111;
    background: #fff;
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.35;
  }
  #${PRINT_ROOT_ID} h1 { font-size: 18pt; margin: 0 0 2mm; }
  #${PRINT_ROOT_ID} h2 { font-size: 13pt; margin: 6mm 0 2mm; border-bottom: 0.4mm solid #111; padding-bottom: 1mm; }
  #${PRINT_ROOT_ID} h3 { font-size: 11pt; margin: 4mm 0 1.5mm; }
  #${PRINT_ROOT_ID} p { margin: 0 0 2mm; }
  #${PRINT_ROOT_ID} .pedra-print-header { border-bottom: 0.6mm solid #111; padding-bottom: 3mm; margin-bottom: 4mm; }
  #${PRINT_ROOT_ID} .pedra-print-subtitle { font-size: 12pt; font-weight: 600; }
  #${PRINT_ROOT_ID} .pedra-print-date { font-size: 9pt; color: #555; }
  #${PRINT_ROOT_ID} .pedra-print-summary {
    display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1mm 6mm; margin-bottom: 3mm;
  }
  #${PRINT_ROOT_ID} .pedra-print-kv {
    display: flex; justify-content: space-between; gap: 4mm;
    border-bottom: 0.2mm dotted #999; padding: 0.8mm 0;
  }
  #${PRINT_ROOT_ID} .pedra-print-kv span { color: #444; }
  #${PRINT_ROOT_ID} .pedra-print-table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; font-size: 9pt; }
  #${PRINT_ROOT_ID} .pedra-print-table th,
  #${PRINT_ROOT_ID} .pedra-print-table td { border: 0.2mm solid #999; padding: 1mm 1.5mm; text-align: left; }
  #${PRINT_ROOT_ID} .pedra-print-table th { background: #eee; font-weight: 600; }
  #${PRINT_ROOT_ID} .pedra-print-table tbody tr:nth-child(even) td { background: #f7f7f7; }
  #${PRINT_ROOT_ID} .pedra-print-empty { font-style: italic; color: #555; }
  #${PRINT_ROOT_ID} .pedra-print-notes { white-space: pre-wrap; border-left: 0.8mm solid #999; padding-left: 3mm; }
  #${PRINT_ROOT_ID} .pedra-print-warnings { margin: 0 0 3mm 5mm; padding: 0; }
  #${PRINT_ROOT_ID} .pedra-print-sheet { break-inside: auto; page-break-before: always; }
  #${PRINT_ROOT_ID} .pedra-print-sheetfoot { font-size: 9pt; color: #333; margin-bottom: 3mm; }
  #${PRINT_ROOT_ID} .pedra-print-diagram { margin: 2mm 0 3mm; text-align: center; break-inside: avoid; }
  #${PRINT_ROOT_ID} .pedra-print-diagram svg { width: 100%; height: auto; max-height: 150mm; }
  #${PRINT_ROOT_ID} .pedra-print-unfit h2 { color: #A11; border-color: #A11; }
  #${PRINT_ROOT_ID} thead { display: table-header-group; }
  #${PRINT_ROOT_ID} tr { break-inside: avoid; }
}
</style>`;
}

/**
 * Monta o HTML completo do relatorio: capa, uma secao por chapa e o bloco das
 * pecas que nao couberam. Nao toca no DOM — util para teste e pre-visualizacao.
 */
export function buildPrintHtml(data: PrintReportData, options: PrintOptions): string {
  const diagrams = new Map<number, PrintDiagram>();
  for (const d of data.diagrams) diagrams.set(d.sheetIndex, d);

  const sheets = data.result.sheets;
  const body: string[] = [buildCover(data, options)];
  for (const sheet of sheets) {
    body.push(buildSheetSection(sheet, sheets.length, diagrams.get(sheet.index), data, options));
  }
  body.push(buildUnfitSection(data));

  if (sheets.length === 0) {
    body.push('<p class="pedra-print-empty">Nenhum plano de corte foi gerado.</p>');
  }

  return `<div class="pedra-print-doc">${body.join('')}</div>`;
}

/** Remove o container de impressao, se existir. */
export function removePrintRoot(): void {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(PRINT_ROOT_ID);
  if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
}

/**
 * Monta o relatorio num container dedicado e abre a caixa de impressao do
 * navegador (de onde sai o PDF). O container e removido assim que a impressao
 * termina — a tela nunca fica suja.
 */
export function printReport(data: PrintReportData, options: Partial<PrintOptions> = {}): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const opts: PrintOptions = { ...DEFAULT_PRINT_OPTIONS, ...options };

  removePrintRoot();
  const root = document.createElement('div');
  root.id = PRINT_ROOT_ID;
  root.setAttribute('aria-hidden', 'true');
  root.innerHTML = buildPrintStyles() + buildPrintHtml(data, opts);
  document.body.appendChild(root);
  document.documentElement.setAttribute('data-printing', 'true');

  let done = false;
  const cleanup = (): void => {
    if (done) return;
    done = true;
    window.removeEventListener('afterprint', cleanup);
    document.documentElement.removeAttribute('data-printing');
    removePrintRoot();
  };

  window.addEventListener('afterprint', cleanup);

  try {
    window.print();
  } catch {
    // jsdom e alguns navegadores embarcados nao implementam print().
    cleanup();
    return;
  }

  // Rede de seguranca para quem nao dispara `afterprint`.
  window.setTimeout(cleanup, 60_000);
}

/**
 * Impressao simples da tela atual, usando as regras `@media print` globais.
 * Para o relatorio completo use `printReport`.
 */
export function printResult(): void {
  if (typeof window === 'undefined' || typeof window.print !== 'function') return;
  try {
    window.print();
  } catch {
    // Ambiente sem suporte a impressao: nada a fazer.
  }
}
